import React, { useState} from 'react'
import { Box, Stack } from '@mui/material'
import RadioButtonGroup from '../../../../../../../../component/RadioButtonGroup'
import MultiSectionClock from '../../../../../../../../component/MultiSectionClock';
import { tokens } from '../../../../../../../../theme';

const daysOfWeek = ['Sunday','Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// a labeled row: title on the left, controls grouped to its right
const Row = ({ label, children, sx }) => (
  <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "flex-start" }} spacing={2}
    sx={{ py: "1.25rem", ...sx }}>
    <Box sx={{ width: { md: "14rem" }, flexShrink: 0, fontSize: "0.875rem", fontWeight: 600, color: tokens.text, pt: "0.5rem" }}>
      {label}
    </Box>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Stack>
);

// caption + clock unit
const TimeField = ({ caption, children }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>{caption}</Box>
    {children}
  </Box>
);

const SetSchedule = ({ shiftData, setShiftData, workingDays, selectedIndex, disabled }) => {

  const [isMaxGrossHoursEnabled, setIsMaxGrossHoursEnabled] = useState(false);

   const onFieldChange = (field, value) => {
    const updatedDays = [...shiftData.days];
    if (shiftData?.doesScheduleVary) {
      updatedDays[selectedIndex] = { ...updatedDays[selectedIndex], [field]: value };
    } else {
      workingDays.forEach(index => {
        updatedDays[index] = { ...updatedDays[index], [field]: value };
      });
    }
    setShiftData(prevData => ({ ...prevData, days: updatedDays }));
  };

  const isDisabled = workingDays?.length === 0;
  const day = (k) => shiftData?.doesScheduleVary ? shiftData?.days[selectedIndex]?.[k] : shiftData?.days[workingDays[0]]?.[k];

  if (isDisabled) return null;

  return (
    <Box sx={{ mt: "1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface, px: "1.25rem" }}>
        {selectedIndex !== undefined && (
          <Box sx={{ pt: "1.25rem", fontSize: "0.8125rem", fontWeight: 600, color: tokens.accent }}>
            Schedule for {!shiftData?.doesScheduleVary ? "All working days" : daysOfWeek[selectedIndex]}
          </Box>
        )}

        {shiftData?.flexibleWorkingHours ? (
          <>
            <Row label="Minimum gross hours required" sx={{ borderBottom: `1px solid ${tokens.border}` }}>
              <RadioButtonGroup
                labelStyle={{ fontSize: "0.875rem", lineHeight: 1.6, color: tokens.text }}
                name="hoursMetric"
                onChange={(e) => onFieldChange('hoursMetric', e.target.value)}
                value={day('hoursMetric')}
                options={[{ value: "gross", label: 'Gross Hour' }, { value: "effective", label: 'Effective Hour' }]}
                disabled={disabled}
              />
              <Stack direction="row" spacing={3} sx={{ mt: "0.75rem" }}>
                <TimeField caption="Full Day">
                  <MultiSectionClock initialHours={day('minFullDayHours')} showMinutes={false}
                    onChangeHours={(value) => onFieldChange('minFullDayHours', value)} disabled={disabled} />
                </TimeField>
                <TimeField caption="Half Day">
                  <MultiSectionClock initialHours={day('minHalfDayHours')} showMinutes={false}
                    onChangeHours={(value) => onFieldChange('minHalfDayHours', value)} disabled={disabled} />
                </TimeField>
              </Stack>
            </Row>

            <Row label="Maximum gross hours">
              <RadioButtonGroup
                labelStyle={{ fontSize: "0.875rem", lineHeight: 1.6, color: tokens.text }}
                value={isMaxGrossHoursEnabled}
                onChange={(e) => setIsMaxGrossHoursEnabled(e.target.value === 'true')}
                options={[{ value: false, label: 'Disable' }, { value: true, label: 'Enable' }]}
                disabled={disabled}
              />
              {isMaxGrossHoursEnabled && (
                <Stack direction="row" spacing={3} sx={{ mt: "0.75rem" }}>
                  <TimeField caption="Full Day">
                    <MultiSectionClock initialHours={day('maxFullDayHours')} showMinutes={false}
                      onChangeHours={(value) => onFieldChange('maxFullDayHours', value)} disabled={disabled} />
                  </TimeField>
                  <TimeField caption="Half Day">
                    <MultiSectionClock initialHours={day('maxHalfDayHours')} showMinutes={false}
                      onChangeHours={(value) => onFieldChange('maxHalfDayHours', value)} disabled={disabled} />
                  </TimeField>
                </Stack>
              )}
            </Row>
          </>
        ) : (
          <Row label="Shift timing">
            <Stack direction="row" spacing={3}>
              <TimeField caption="Start Time">
                <MultiSectionClock
                  initialHours={Math.floor(day('startTime') / 60)}
                  initialMinutes={Math.floor(day('startTime') % 60)}
                  onChangeTime={(value) => onFieldChange('startTime', value)}
                  disabled={disabled} />
              </TimeField>
              <TimeField caption="End Time">
                <MultiSectionClock
                  initialHours={Math.floor(day('endTime') / 60)}
                  initialMinutes={Math.floor(day('endTime') % 60)}
                  onChangeTime={(value) => onFieldChange('endTime', value)}
                  disabled={disabled} />
              </TimeField>
            </Stack>
          </Row>
        )}

        <Row label="Break time for the shift" sx={{ borderTop: `1px solid ${tokens.border}` }}>
          <MultiSectionClock
            initialHours={Math.floor(day('breakTime') / 60)}
            initialMinutes={Math.floor(day('breakTime') % 60)}
            onChangeTime={(value) => onFieldChange('breakTime', value)}
            disabled={disabled} />
        </Row>
    </Box>
  )
}

export default SetSchedule
